import { keyBy } from "lodash";

import {
  BlockCellId,
  CellId,
  CellType,
  FRACTIONAL_INDEX_END,
  FRACTIONAL_INDEX_START,
  getIndicesBetween,
} from "../index.js";

import {
  CellLocation,
  ChildCellLocation,
  GlobalCellLocation,
  RelativeCellLocation,
  ResolvedCellLocation,
} from "./CellLocation.js";

export interface DereferenceChildCellOptions {
  /**
   * Trying to insert/move a cell into an existing component import cell
   * is usually a mistake, so by default we "dereference" the children
   * to the parent cell to prevent this common mistake
   *
   * @default true
   */
  dereferenceComponentImportCellChildren?: boolean;
  /**
   * Trying to insert/move a cell into an existing SQL block cell
   * is usually a mistake, so by default we "dereference" the children
   * to the parent cell to prevent this common mistake
   *
   * @default true
   */
  dereferenceSqlBlockCellChildren?: boolean;
}

interface CellFragment {
  id: CellId;
  parentCellId: CellId | null;
  order: string;
  cellType: CellType;
  blockCellId: BlockCellId | null;
}

interface ResolveCellLocationCommonArgs<
  TCell extends CellFragment,
> extends DereferenceChildCellOptions {
  count: number;
  /**
   * A sorted and flattened list of cells in the notebook.
   */
  cells: TCell[];
  /**
   * A list of currently selected cell ids.
   * As of this writing, this is only supported in the client.
   */
  selectedCellIds?: CellId[] | null | undefined;
  checkIsSqlBlockCell: (cell: TCell) => boolean;
}

/**
 * Given a single {@link CellLocation} find multiple {@link ResolvedCellLocation}
 * at that point
 */
export function resolveCellLocations<TCell extends CellFragment>({
  count,
  location,
  ...args
}: ResolveCellLocationCommonArgs<TCell> & {
  location: CellLocation;
}): ResolvedCellLocation[] {
  if (location.type === "global") {
    return resolveGlobalCellLocations({ count, location, ...args });
  } else if (location.type === "child") {
    return resolveChildCellLocations({ count, location, ...args });
  } else {
    return resolveRelativeCellLocations({ count, location, ...args });
  }
}

function resolveRelativeCellLocations<TCell extends CellFragment>({
  cells,
  checkIsSqlBlockCell,
  count,
  dereferenceComponentImportCellChildren = true,
  dereferenceSqlBlockCellChildren = true,
  location,
  selectedCellIds,
}: ResolveCellLocationCommonArgs<TCell> & {
  location: RelativeCellLocation;
}): ResolvedCellLocation[] {
  const cellsById = keyBy(cells, "id");
  const targetCell = cellsById[location.targetCellId];

  if (targetCell == null) {
    throw new Error(`Unable to find target cell ${location.targetCellId}`);
  }

  const parentCellId = targetCell.parentCellId ?? null;
  const parentCell = parentCellId != null ? cellsById[parentCellId] : undefined;

  if (parentCell != null) {
    const componentImportCell = dereferenceComponentImportCellChildren
      ? recursivelyFindComponentImportCell({
          cell: parentCell,
          cellsById,
        })
      : null;

    if (componentImportCell != null) {
      return resolveCellLocations({
        count,
        location: {
          type: "relative",
          targetCellId: componentImportCell.id,
          position: location.position,
        },
        dereferenceComponentImportCellChildren,
        dereferenceSqlBlockCellChildren,
        cells,
        selectedCellIds,
        checkIsSqlBlockCell,
      });
    }

    const isSqlBlockCell =
      dereferenceSqlBlockCellChildren && checkIsSqlBlockCell(parentCell);

    if (isSqlBlockCell) {
      return resolveCellLocations({
        count,
        location: {
          type: "relative",
          targetCellId: parentCell.id,
          position: location.position,
        },
        dereferenceComponentImportCellChildren,
        dereferenceSqlBlockCellChildren,
        cells,
        selectedCellIds,
        checkIsSqlBlockCell,
      });
    }
  }

  const siblingCells = cells.filter(
    (c) =>
      c.parentCellId === parentCellId ||
      (c.parentCellId == null && parentCellId == null),
  );
  const targetCellIndex = siblingCells.findIndex((c) => c.id === targetCell.id);

  if (targetCellIndex === -1) {
    throw new Error(
      `Unable to find target cell ${location.targetCellId} in sibling cells`,
    );
  }

  const orders =
    location.position === "before"
      ? getIndicesBetween({
          start:
            siblingCells[targetCellIndex - 1]?.order ?? FRACTIONAL_INDEX_START,
          end: targetCell.order,
          count,
        })
      : getIndicesBetween({
          start: targetCell.order,
          end: siblingCells[targetCellIndex + 1]?.order ?? FRACTIONAL_INDEX_END,
          count,
        });

  return orders.map((order) => ({
    order,
    parentCellId,
    parentBlockCellId: parentCell?.blockCellId ?? null,
    parentComponentImportCellId: null,
  }));
}

function resolveChildCellLocations<TCell extends CellFragment>({
  cells,
  checkIsSqlBlockCell,
  count,
  dereferenceComponentImportCellChildren = true,
  dereferenceSqlBlockCellChildren = true,
  location,
  selectedCellIds,
}: ResolveCellLocationCommonArgs<TCell> & {
  location: ChildCellLocation;
}): ResolvedCellLocation[] {
  const cellsById = keyBy(cells, "id");
  const parentCell =
    location.parentCellId != null
      ? cellsById[location.parentCellId]
      : undefined;

  if (parentCell != null) {
    const componentImportCell = dereferenceComponentImportCellChildren
      ? recursivelyFindComponentImportCell({
          cell: parentCell,
          cellsById,
        })
      : null;

    if (componentImportCell != null) {
      return resolveCellLocations({
        count,
        location: {
          type: "relative",
          targetCellId: componentImportCell.id,
          position: location.position === "first" ? "before" : "after",
        },
        dereferenceComponentImportCellChildren,
        dereferenceSqlBlockCellChildren,
        cells,
        selectedCellIds,
        checkIsSqlBlockCell,
      });
    }

    const isSqlBlockCell =
      dereferenceSqlBlockCellChildren && checkIsSqlBlockCell(parentCell);

    if (isSqlBlockCell) {
      return resolveCellLocations({
        count,
        location: {
          type: "relative",
          targetCellId: parentCell.id,
          position: location.position === "first" ? "before" : "after",
        },
        dereferenceComponentImportCellChildren,
        dereferenceSqlBlockCellChildren,
        cells,
        selectedCellIds,
        checkIsSqlBlockCell,
      });
    }
  }

  const siblingCells = cells.filter(
    (c) =>
      c.parentCellId === location.parentCellId ||
      (location.parentCellId == null && c.parentCellId == null),
  );

  if (siblingCells.length === 0) {
    const orders = getIndicesBetween({
      start: FRACTIONAL_INDEX_START,
      end: FRACTIONAL_INDEX_END,
      count,
    });
    return orders.map((order) => ({
      order,
      parentCellId: location.parentCellId,
      parentBlockCellId: parentCell?.blockCellId ?? null,
      parentComponentImportCellId: null,
    }));
  } else if (location.position === "first") {
    return resolveCellLocations({
      count,
      location: {
        type: "relative",
        targetCellId: siblingCells[0]!.id,
        position: "before",
      },
      dereferenceComponentImportCellChildren,
      dereferenceSqlBlockCellChildren,
      cells,
      selectedCellIds,
      checkIsSqlBlockCell,
    });
  } else {
    return resolveCellLocations({
      count,
      location: {
        type: "relative",
        targetCellId: siblingCells[siblingCells.length - 1]!.id,
        position: "after",
      },
      dereferenceComponentImportCellChildren,
      dereferenceSqlBlockCellChildren,
      cells,
      selectedCellIds,
      checkIsSqlBlockCell,
    });
  }
}

function resolveGlobalCellLocations<TCell extends CellFragment>({
  count,
  location,
  selectedCellIds,
  ...args
}: ResolveCellLocationCommonArgs<TCell> & {
  location: GlobalCellLocation;
}): ResolvedCellLocation[] {
  return resolveCellLocations({
    count,
    location: normalizeCellLocation({
      location,
      selectedCellIds,
    }),
    selectedCellIds,
    ...args,
  });
}

function recursivelyFindComponentImportCell({
  cell,
  cellsById,
}: {
  cell: CellFragment;
  cellsById: Record<CellId, CellFragment>;
}): CellFragment | null {
  if (cell.cellType === CellType.COMPONENT_IMPORT) {
    return cell;
  } else if (cell.parentCellId) {
    const parentCell = cellsById[cell.parentCellId];

    if (parentCell == null) {
      throw new Error(`Unable to find parent cell ${cell.parentCellId}`);
    }
    return recursivelyFindComponentImportCell({
      cell: parentCell,
      cellsById,
    });
  } else {
    return null;
  }
}

export function normalizeCellLocation({
  location,
  selectedCellIds,
}: {
  location: CellLocation;
  selectedCellIds: CellId[] | null | undefined;
}): RelativeCellLocation | ChildCellLocation {
  if (location.type !== "global") {
    return location;
  }

  const firstSelectedCellId = selectedCellIds?.[0];

  if (firstSelectedCellId != null) {
    return {
      type: "relative",
      targetCellId: firstSelectedCellId,
      position: "after",
    };
  } else {
    return {
      type: "child",
      parentCellId: null,
      position: "last",
    };
  }
}
